import { useState, useCallback, useEffect } from "react";
import { isTestEnv } from "../globals";
import { useUser } from "../contexts/UserContext";
import { useAllBranches } from "../contexts/BranchesContext";
import ProductFamily from "../components/ProductFamily";
import Customers from "../components/Customers";
import GeneralInfrastructure from "../components/General infrastructure";
import translation from "../translations/permissionsTranslations";
import { protectedResources } from "../authConfig";
import useFetchWithMsal from "../hooks/useFetchWithMsal";

if (!translation.allBranchesAccess) {
  translation.allBranchesAccess = "הרשאה לכל הסניפים";
}

const translations = {
  branch: "סניף",
  ordererCustomer: "לקוח מזמין",
  intOrderType: "סוג הזמנה פנימי",
  extOrderType: "סוג הזמנה חיצוני",
  family: "משפחות מוצר",
  receiveCustomer: "לקוחות מקבלים",
};

export const usePermissions = ({ backendUrl }) => {
  const [tabValue, setTabValue] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [rolesList, setRolesList] = useState([]);
  const { user, setUser } = useUser();
  const [loading, setLoading] = useState(false);

  const [adminPages, setAdminPages] = useState([]);
  const { allBranches } = useAllBranches();

  const [showAddRoleForm, setShowAddRoleForm] = useState(false);
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [editUserId, setEditUserId] = useState(null);
  const [innerTabValue, setInnerTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiScopes.scopes.read,
  });
  const [mockDropdownData, setMockDropdownData] = useState({
    branch: [],
    ordererCustomer: [],
    intOrderType: [],
    extOrderType: [],
    family: [],
    receiveCustomer: [],
  });

  const tabs = [
    {
      label: "משפחות מוצר",
      component: (
        <ProductFamily
          rolesList={rolesList}
          setMockDropdownData={setMockDropdownData}
          execute={execute}
          setSnackbar={setSnackbar}
        />
      ),
      condition: user?.roles?.some((r) => r.SalesPromotionsPage || r.admin),
    },
    {
      label: "לקוחות",
      component: (
        <Customers
          mockDropdownData={mockDropdownData}
          setMockDropdownData={setMockDropdownData}
          execute={execute}
          setSnackbar={setSnackbar}
        />
      ),
      condition: user?.roles?.some((r) => r.SalesPromotionsPage || r.admin),
    },
    {
      label: "כללי",
      component: (
        <GeneralInfrastructure
          setMockDropdownData={setMockDropdownData}
          execute={execute}
        />
      ),
      condition: true,
    },
  ];
  const [infrastructureTabs, setInfrastructureTabs] = useState([]);
  const [filteredTabs, setFilteredTabs] = useState(
    infrastructureTabs.filter((tab) => tab.condition),
  );

  const pageNames = [
    "HomePage",
    "AproveLinesPage",
    "ObligoExceptionPage",
    "ViewObligoData",
    "CheckInvoicesPage",
    "SalesPromotionsPage",
    "PermissionsPage",
    "PurchaseInvoicePage",
    "TimeLinePage",
    "EldanPrinterPage",
    "ChemoRecordsPage",
    "ImportManagementPage",
    "emailTrackingPage",
    "ResearchMonitorPage",
    "ResearchMonitorRole",
    "FuelControlPage",
    "HRCVMatchingPage",
    "PromotionsSystemPage",
    "QrCodeDeviceMappingPage",
    "TradeDelayPage",
    "AgentComplaintsPage",
  ];

  const extractAdminPages = useCallback(() => {
    if (!user || Object.keys(user).length === 0) return [];

    if (user.roles?.some((role) => role.admin === true)) {
      return pageNames;
    }

    const adminPages = new Set();
    user.roles?.forEach((role) => {
      Object.entries(role).forEach(([field, value]) => {
        if (role.PermissionsPage === true && pageNames.includes(field)) {
          if (value === true) {
            adminPages.add(field);
          }
          // handle string-based permission fields (e.g. ResearchMonitorRole)
          if (
            typeof value === "string" &&
            value !== "none" &&
            value !== "false"
          ) {
            adminPages.add(field);
          }
        }
      });
    });

    return Array.from(adminPages);
  }, [user]);

  useEffect(() => {
    if (user && Object.keys(user).length > 0) {
      const x = extractAdminPages();
      setAdminPages(extractAdminPages());
      setInfrastructureTabs(tabs);
      setFilteredTabs(tabs.filter((tab) => tab.condition));
      const availableTabs = tabs.filter((tab) => tab.condition);
      if (!availableTabs[innerTabValue]) {
        setInnerTabValue(0);
      }
    }
  }, [user, rolesList]);

  useEffect(() => {
    if (
      !loading &&
      (Object.keys(user).length === 0 ||
        adminPages.length === 0 ||
        mockDropdownData.branch.length === 0)
    ) {
      setLoading(true);
    }

    if (loading && user.roles?.some((r) => r.admin || r.SalesPromotionsPage)) {
      const getData = async () => {
        try {
          const salesPromotionsData = await execute(
            "GET",
            `${process.env.REACT_APP_BACKEND_BASE_URL}/api/salesPromotions/getDataOfPermissions`,
          );

          setMockDropdownData({
            branch: allBranches,
            ordererCustomer: salesPromotionsData.orderingCustomers,
            intOrderType: salesPromotionsData.internalOrderTypes,
            extOrderType: salesPromotionsData.externalOrderTypes,
            family: salesPromotionsData.productFamily,
            receiveCustomer: salesPromotionsData.recivingCustomer,
          });
          setLoading(false);
        } catch (err) {
          setLoading(false);
          alert(
            `${translation.somethingWentWrong} ${err.status === 403 ? "\n" + translation.checkUserPermissions : ""}`,
          );
        }
      };
      getData();
    }
  }, [loading, user, adminPages, mockDropdownData.branch.length]);

  const handleAccordionChange = (roleId) => (event, isExpanded) => {
    setExpanded(isExpanded ? roleId : false);
  };

  const handleInnerTabChange = (event, newValue) => {
    setSnackbar((prev) => ({ ...prev, open: false }));
    setInnerTabValue(newValue);
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCheckboxChange = (roleName, field) => async (event) => {
    const value =
      event.target.checked !== undefined
        ? event.target.checked
        : event.target.value;
    const roleIndex = rolesList.findIndex((role) => role.Name === roleName);
    if (roleIndex === -1) return; // Role not found, handle error
    const updatedRolesList = [...rolesList];

    // Handle obligo access level select
    if (field === "ObligoExceptionPage" && typeof value === "string") {
      if (value === "none") {
        updatedRolesList[roleIndex]["ObligoExceptionPage"] = false;
        updatedRolesList[roleIndex]["ViewObligoData"] = false;
      } else if (value === "readonly") {
        updatedRolesList[roleIndex]["ObligoExceptionPage"] = false;
        updatedRolesList[roleIndex]["ViewObligoData"] = true;
      } else if (value === "full") {
        updatedRolesList[roleIndex]["ObligoExceptionPage"] = true;
        updatedRolesList[roleIndex]["ViewObligoData"] = false;
      }
      // Handle research monitor role select
    } else if (field === "ResearchMonitorRole" && typeof value === "string") {
      if (value === "none") {
        updatedRolesList[roleIndex]["ResearchMonitorRole"] = false;
        updatedRolesList[roleIndex]["ResearchMonitorPage"] = false;
      } else {
        updatedRolesList[roleIndex]["ResearchMonitorRole"] = value;
        updatedRolesList[roleIndex]["ResearchMonitorPage"] = true;
      }
    } else {
      updatedRolesList[roleIndex][field] = value;
    }

    setRolesList(updatedRolesList);

    // Prepare the updated role object
    const updatedRole = {
      ...rolesList[roleIndex],
      ...(field === "ObligoExceptionPage" && typeof value === "string"
        ? {
            ObligoExceptionPage: value === "full",
            ViewObligoData: value === "readonly",
          }
        : field === "ResearchMonitorRole" && typeof value === "string"
          ? {
              ResearchMonitorRole: value === "none" ? false : value,
              ResearchMonitorPage: value !== "none",
            }
          : {
              [field]: value,
            }),
    };

    try {
      await execute(
        "POST",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/roles/updaterole`,
        updatedRole,
      );
      const roleInd = user.roles.findIndex((r) => r._id === updatedRole._id);
      if (roleInd !== -1) {
        const updatedRoles = user.roles.map((r) =>
          r._id === updatedRole._id ? updatedRole : r,
        );

        const hasFieldInOtherRole = updatedRoles.some(
          (r) => r._id !== updatedRole._id && r[field] === true,
        );
      }

      setSnackbar({
        open: true,
        message: translation.roleUpdateSuccess,
        severity: "success",
      });
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: translation.roleUpdateError,
        severity: "error",
      });
    }
  };

  const handleDeleteRole = async (roleId) => {
    try {
      const isSure = window.confirm(translation.deleteRoleConfirm);
      if (isSure) {
        await execute(
          "DELETE",
          `${process.env.REACT_APP_BACKEND_BASE_URL}/api/roles/deleterole/${roleId}`,
        );
        setRolesList(rolesList.filter((role) => role._id !== roleId));
        setSnackbar({
          open: true,
          message: translation.roleDeleteSuccess,
          severity: "success",
        });
      }
    } catch (error) {
      isTestEnv && console.error(error);
      setSnackbar({
        open: true,
        message: translation.generalError,
        severity: "error",
      });
    }
  };

  const handleAddRole = async (roleName, selectedPages) => {
    try {
      const pagePermissions = {};
      selectedPages.forEach((page) => {
        pagePermissions[page] = true;
      });

      const response = await execute(
        "POST",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/roles/addrole`,
        {
          Name: roleName,
          ...pagePermissions,
          selectedPages: selectedPages,
          Branches: [],
        },
      );

      const savedRole = response;
      isTestEnv && console.log("savedRole", savedRole);
      setRolesList([...rolesList, savedRole]);
      setShowAddRoleForm(false);
      setSnackbar({
        open: true,
        message: translation.roleAddSuccess,
        severity: "success",
      });
    } catch (error) {
      isTestEnv && console.error(error);
      if (error.status === 409)
        setSnackbar({
          open: true,
          message: translation.duplicateRoleError,
          severity: "error",
        });
      else
        setSnackbar({
          open: true,
          message: translation.generalError,
          severity: "error",
        });
    }
  };

  const handleDeleteUser = async (userId) => {
    const isSure = window.confirm(translation.deleteUserConfirm);
    if (isSure) {
      try {
        await execute(
          "DELETE",
          `${process.env.REACT_APP_BACKEND_BASE_URL}/api/users/deleteuser/${userId}`,
        );
        setUsersList(usersList.filter((user) => user._id !== userId));
        setSnackbar({
          open: true,
          message: translation.roleDeleteSuccess,
          severity: "success",
        });
      } catch (error) {
        isTestEnv && console.error(error);
        setSnackbar({
          open: true,
          message: translation.generalError,
          severity: "error",
        });
      }
    }
  };

  const handleUpdateUser = async (updatedUser) => {
    try {
      const response = await execute(
        "POST",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/users/updateUser`,
        updatedUser,
      );
      const copyUpdateUser = updatedUser;
      copyUpdateUser.Role = Object.keys(copyUpdateUser.Role);
      setUsersList((prevUsersList) =>
        prevUsersList.map((user) =>
          user._id === copyUpdateUser._id ? copyUpdateUser : user,
        ),
      );
      setEditUserId(null);
      setSnackbar({
        open: true,
        message: translation.userUpdateSuccess,
        severity: "success",
      });
      isTestEnv && console.log("updatedUserList", usersList);
    } catch (error) {
      isTestEnv && console.error(error);
      setSnackbar({
        open: true,
        message: translation.generalError,
        severity: "error",
      });
    }
  };

  const handleAddUser = async (user) => {
    try {
      const roleIds = user.Role.map((roleName) => {
        const role = rolesList.find((role) => role?.Name === roleName);
        return role ? role._id : null;
      }).filter((roleId) => roleId !== null);

      // Assign the role IDs to updatedUser
      user.Role = roleIds;
      if (!user.Branches || user.Branches.length === 0) {
      }

      const response = await execute(
        "POST",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/users/adduser`,
        user,
      );
      isTestEnv && console.log(response.savedUser);
      const savedUser = response.savedUser;
      setUsersList([...usersList, savedUser]);
      setShowAddUserForm(false);
      setSnackbar({
        open: true,
        message: translation.userAddSuccess,
        severity: "success",
      });
    } catch (error) {
      isTestEnv &&
        console.error(
          "Error details:",
          error.response ? error.response.data : error.message,
        );
      setSnackbar({
        open: true,
        message: error.response
          ? error.response.data.error
          : "An error occurred",
        severity: "error",
      });
    }
  };

  useEffect(() => {
    isTestEnv && console.log("hii from useEffect");
    async function fetchData() {
      try {
        const rolesResponse = await execute(
          "GET",
          `${process.env.REACT_APP_BACKEND_BASE_URL}/api/roles/allroles`,
        );
        console.log(368, rolesResponse.data);

        setRolesList(rolesResponse.data);
        const usersResponse = await execute(
          "GET",
          `${process.env.REACT_APP_BACKEND_BASE_URL}/api/users/allusers`,
        );
        setUsersList(usersResponse.data);
        isTestEnv && console.log("usersResponse.data", usersResponse.data);
      } catch (error) {
        isTestEnv && console.error(error);
      }
    }

    fetchData();
  }, [execute]);

  const handleTabChange = (event, newValue) => {
    setSnackbar((prev) => ({ ...prev, open: false }));
    setTabValue(newValue);
    setSnackbar({ ...snackbar, open: false });
  };

  const getDisplayValue = (key, value) => {
    if (key === "branch") {
      const branchItem =
        allBranches.length &&
        allBranches.find((branch) => branch.BRANCHNAME === value);
      return branchItem
        ? `${branchItem.BRANCHDES} ${value}`
        : translation.notSpecified;
    }

    if (key === "family" && Array.isArray(value)) {
      return value
        .map((item) => {
          if (typeof item !== "object" || item === null) return item;
          const full = mockDropdownData.family?.find((f) => f._id === item._id);
          return full ? `${full.familyName} - ${full.familyDes}` : item.des || translation.notSpecified;
        })
        .join(", ");
    }

    if (Array.isArray(value)) {
      return value
        .map((item) =>
          typeof item === "object" && item !== null
            ? item.des || translation.notSpecified
            : item,
        )
        .join(", ");
    }

    if (typeof value === "object") {
      return JSON.stringify(value);
    }

    return value || translation.notSpecified;
  };

  const disabledCheckbox = (page) => {
    if (user?.accessRights.admin) return false;
    return !adminPages.includes(page);
  };

  const translateKey = (key) => translations[key] || key;

  const handleBranchChange = useCallback((role, branches) => {
    const updatedRole = { ...role, Branches: branches };
    return updatedRole;
  }, []);

  const updatePermissions = useCallback(
    async (roleId, permissions) => {
      try {
        const role = rolesList.find((r) => r._id === roleId);
        if (!role) {
          throw new Error(translation.roleNotFound);
        }

        const updatedRole = { ...role, Permissions: permissions };

        await execute(
          "POST",
          `${backendUrl}/api/roles/updaterole`,
          updatedRole,
        );

        setRolesList((prevRolesList) =>
          prevRolesList.map((role) =>
            role._id === roleId ? updatedRole : role,
          ),
        );

        setSnackbar({
          open: true,
          message: translation.permissionsUpdateSuccess,
          severity: "success",
        });
      } catch (error) {
        isTestEnv && console.error(error);
        setSnackbar({
          open: true,
          message: translation.permissionsUpdateError,
          severity: "error",
        });
      }
    },
    [rolesList, backendUrl, execute],
  );

  const updateSalesPromotions = useCallback((name, role) => {
    if (!role.properties?.length) {
      return {
        ...role,
        properties: [
          {
            SalesPromotions: {
              [name]: true,
            },
          },
        ],
      };
    }

    if (!role.properties[0]?.SalesPromotions) {
      return {
        ...role,
        properties: [
          {
            ...role.properties[0],
            SalesPromotions: {
              [name]: true,
            },
          },
          ...role.properties.slice(1),
        ],
      };
    }

    const updatedRole = {
      ...role,
      properties: [
        {
          ...role.properties[0],
          SalesPromotions: {
            ...role.properties[0].SalesPromotions,
            [name]: !role.properties[0].SalesPromotions[name],
          },
        },
        ...role.properties.slice(1),
      ],
    };

    return updatedRole;
  }, []);

  const updateRole = useCallback(
    async (event, role1) => {
      const { name, value, checked } = event.target;
      let updatedRole;
      switch (name) {
        case "OrderApprover":
        case "OrderCreator":
          updatedRole = updateSalesPromotions(name, role1);
          break;
        case "Branches":
          updatedRole = handleBranchChange(role1, event.target.value);
        default:
          break;
      }
      try {
        const x = await execute(
          "POST",
          `${process.env.REACT_APP_BACKEND_BASE_URL}/api/roles/updaterole`,
          updatedRole,
        );

        setRolesList((prevRolesList) =>
          prevRolesList.map((role) =>
            role._id === updatedRole._id ? updatedRole : role,
          ),
        );
        setSnackbar({
          open: true,
          message: "הרשאות התפקיד עודכנו בהצלחה .",
          severity: "success",
        });
      } catch (error) {
        isTestEnv && console.error(138, error);
        setSnackbar({
          open: true,
          message: "ארעה שגיאה בעדכון התפקיד",
          severity: "error",
        });
      }
    },
    [rolesList, backendUrl, execute],
  );

  return {
    updateRole,
    translation,
    tabValue,
    user,
    usersList,
    expanded,
    innerTabValue,
    filteredTabs,
    handleInnerTabChange,
    handleDeleteUser,
    execute,
    rolesList,
    setRolesList,
    snackbar,
    setSnackbar,
    editUserId,
    setEditUserId,
    showAddRoleForm,
    setShowAddRoleForm,
    showAddUserForm,
    setShowAddUserForm,
    mockDropdownData,
    setMockDropdownData,
    allBranches,
    adminPages,
    translateKey,
    handleCheckboxChange,
    handleAccordionChange,
    handleAddUser,
    pageNames,
    handleAddRole,
    handleTabChange,
    disabledCheckbox,
    getDisplayValue,
    handleDeleteRole,
    handleUpdateUser,
  };
};
